import { Controller } from "@hotwired/stimulus"

// Regenerates the client welcome link via the welcome_link JSON endpoint
// and updates the link input, copy widgets, expiry date, and email body preview.
//
// Usage:
//   data-controller="regenerate-link"
//   data-regenerate-link-url-value="/clients/123/welcome_link"
export default class extends Controller {
  static values = {
    url: String,
    regenerating: { type: String, default: "Regenerating..." },
    done: { type: String, default: "Done" },
    failed: { type: String, default: "Failed — try again" },
    regenerateLink: { type: String, default: "Regenerate link" }
  }
  static targets = ["button", "linkInput", "linkCopy", "expiry", "emailBody", "emailBodyCopy"]

  async regenerate() {
    this.buttonTarget.textContent = this.regeneratingValue
    this.buttonTarget.classList.add("pointer-events-none", "opacity-50")

    try {
      const response = await fetch(this.urlValue, {
        headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken }
      })

      if (!response.ok) throw new Error(`Server error: ${response.status}`)

      const { link } = await response.json()
      const oldLink = this.linkInputTarget.value

      this.linkInputTarget.value = link
      // Reach into CopyWidgetComponent's element — targets can't cross controller boundaries
      this.linkCopyTarget.querySelector("[data-copy-text-value]").dataset.copyTextValue = link
      this.updateExpiry()
      this.updateEmailBody(oldLink, link)

      this.buttonTarget.textContent = this.doneValue
      this.buttonTarget.classList.remove("opacity-50")
      setTimeout(() => {
        this.buttonTarget.textContent = this.regenerateLinkValue
        this.buttonTarget.classList.remove("pointer-events-none")
      }, 1500)
    } catch (err) {
      console.error("Failed to regenerate link:", err)
      this.buttonTarget.textContent = this.failedValue
      this.buttonTarget.classList.remove("pointer-events-none", "opacity-50")
      setTimeout(() => { this.buttonTarget.textContent = this.regenerateLinkValue }, 2000)
    }
  }

  updateExpiry() {
    const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    const formatted = expires.toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric"
    }) + " at " + expires.toLocaleTimeString("en-US", {
      hour: "numeric", minute: "2-digit"
    })
    this.expiryTarget.querySelector("strong").textContent = formatted
  }

  updateEmailBody(oldLink, newLink) {
    const pre = this.emailBodyTarget
    pre.textContent = pre.textContent.replace(oldLink, newLink)
    // Reach into CopyWidgetComponent's element — targets can't cross controller boundaries
    this.emailBodyCopyTarget.querySelector("[data-copy-text-value]").dataset.copyTextValue = pre.textContent
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
